import { useClubLogo } from '../lib/useClubLogo';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Users, Trophy, Plus, Pencil, Trash2, LogOut, ArrowLeft,
  Calendar, Upload, X, BarChart3, Shield as ShieldIcon
} from 'lucide-react';
import { adminApi, publicApi, getImageUrl } from '../lib/api';
import { toast } from 'sonner';



// ─── Main Dashboard ───
export default function AdminDashboardPage() {
  const LOGO_URL = useClubLogo();
  const [activeTab, setActiveTab] = useState('players');
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin'); return; }
    adminApi.getStats().then(res => setStats(res.data)).catch(() => {
      localStorage.removeItem('admin_token');
      navigate('/admin');
    });
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    toast.success('Abgemeldet');
    navigate('/admin');
  };

  const tabs = [
    { key: 'players', label: 'Spieler', icon: Users },
    { key: 'teams', label: 'Mannschaften', icon: ShieldIcon },
    { key: 'games', label: 'Spiele', icon: Trophy },
    { key: 'sponsors', label: 'Sponsoren', icon: BarChart3 },
    { key: 'gallery', label: 'Galerie', icon: Calendar },
    { key: 'stats', label: 'Statistik', icon: BarChart3 },
  ];

  return (
    <div className="min-h-screen bg-[hsl(0,0%,5%)]">
      <header className="bg-[hsl(0,0%,5%)]/95 backdrop-blur border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <a href="/" className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full overflow-hidden bg-[hsl(0,0%,12%)]">
                  <img src={LOGO_URL} alt="Logo" className="w-full h-full object-contain rounded-full" style={{ background: '#141414' }} />
                </div>
              </a>
              <div className="border-l border-white/10 pl-4">
                <h1 className="text-sm font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>Admin Dashboard</h1>
                <p className="text-xs text-white/40">EHV Dresden Devils</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <a href="/" className="p-2 text-white/50 hover:text-white/80 rounded-lg hover:bg-white/5 transition-colors duration-200"><ArrowLeft className="w-4 h-4" /></a>
              <button onClick={handleLogout} className="p-2 text-white/50 hover:text-red-400 rounded-lg hover:bg-white/5 transition-colors duration-200" data-testid="admin-logout-button"><LogOut className="w-4 h-4" /></button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Club Logo + Hero BG Upload */}
        <ClubLogoUpload logoUrl={LOGO_URL} />
        <HeroBgUpload />

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[
              { icon: Users, label: 'Spieler', value: stats.totalPlayers, color: 'text-[#46C7FF]' },
              { icon: ShieldIcon, label: 'Teams', value: stats.totalTeams || 0, color: 'text-green-400' },
              { icon: Trophy, label: 'Spiele', value: stats.totalGames, color: 'text-[#CC0000]' },
              { icon: BarChart3, label: 'S/N/U', value: `${stats.wins}/${stats.losses}/${stats.draws}`, color: 'text-yellow-400' },
            ].map((stat, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                className="bg-[hsl(0,0%,8%)] border border-white/10 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <stat.icon className={`w-5 h-5 ${stat.color}`} />
                  <div>
                    <div className="text-xl font-bold text-white font-mono-score">{stat.value}</div>
                    <div className="text-xs text-white/40">{stat.label}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        <div className="flex gap-1 mb-6 bg-[hsl(0,0%,8%)] border border-white/10 rounded-lg p-1 w-fit flex-wrap">
          {tabs.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2 text-sm rounded-md font-medium transition-colors duration-200 ${activeTab === tab.key ? 'bg-[#CC0000] text-white' : 'text-white/60 hover:text-white/80 hover:bg-white/5'}`}
              data-testid={`admin-tab-${tab.key}`}>
              <tab.icon className="w-4 h-4" />{tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'players' && <PlayersTab />}
        {activeTab === 'teams' && <TeamsTab />}
        {activeTab === 'games' && <GamesTab />}
        {activeTab === 'sponsors' && <SponsorsTab />}
        {activeTab === 'gallery' && <GalleryTab />}
        {activeTab === 'stats' && <StatsTab />}
      </div>
    </div>
  );
}


// ─── Players Tab (unchanged from before) ───
function PlayersTab() {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);

  const loadPlayers = useCallback(() => {
    publicApi.getPlayers().then(res => { setPlayers(res.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);
  useEffect(() => { loadPlayers(); }, [loadPlayers]);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Spieler "${name}" wirklich löschen?`)) return;
    try { await adminApi.deletePlayer(id); toast.success(`${name} gelöscht`); loadPlayers(); } catch { toast.error('Fehler beim Löschen'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>Spieler verwalten</h2>
        <button onClick={() => { setEditingPlayer(null); setShowForm(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-[#CC0000] text-white text-sm font-semibold rounded-lg hover:bg-[#B30000] active:scale-[0.98] transition-colors duration-200"
          data-testid="admin-players-create-button">
          <Plus className="w-4 h-4" />Spieler hinzufügen
        </button>
      </div>
      {showForm && <PlayerForm player={editingPlayer} onClose={() => { setShowForm(false); setEditingPlayer(null); }} onSave={() => { setShowForm(false); setEditingPlayer(null); loadPlayers(); }} />}
      {loading ? <div className="bg-[hsl(0,0%,8%)] border border-white/10 rounded-xl p-8 animate-pulse"><div className="h-6 bg-white/5 rounded w-1/3" /></div>
       : players.length === 0 ? <div className="bg-[hsl(0,0%,8%)] border border-white/10 rounded-xl p-16 text-center"><Users className="w-12 h-12 text-white/15 mx-auto mb-4" /><p className="text-white/50 text-sm">Noch keine Spieler</p></div>
       : (
        <div className="bg-[hsl(0,0%,8%)] border border-white/10 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr className="border-b border-white/10">
                <th className="text-left text-xs text-white/40 font-medium px-4 py-3">Foto</th>
                <th className="text-left text-xs text-white/40 font-medium px-4 py-3">#</th>
                <th className="text-left text-xs text-white/40 font-medium px-4 py-3">Name</th>
                <th className="text-left text-xs text-white/40 font-medium px-4 py-3">Position</th>
                <th className="text-right text-xs text-white/40 font-medium px-4 py-3">Aktionen</th>
              </tr></thead>
              <tbody>
                {players.map(p => (
                  <tr key={p._id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="px-4 py-3"><div className="w-10 h-10 rounded-lg overflow-hidden bg-[hsl(0,0%,12%)]">{p.photoUrl ? <img src={getImageUrl(p.photoUrl)} alt={p.name} className="w-full h-full object-cover" /> : <Users className="w-4 h-4 text-white/20 m-3" />}</div></td>
                    <td className="px-4 py-3 text-sm font-bold text-white font-mono-score">#{p.number}</td>
                    <td className="px-4 py-3 text-sm font-medium text-white">{p.name}</td>
                    <td className="px-4 py-3"><span className="text-xs text-white/60 bg-white/5 px-2 py-1 rounded-full">{p.position}</span></td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => { setEditingPlayer(p); setShowForm(true); }} className="p-2 text-white/40 hover:text-[#46C7FF] rounded-lg"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => handleDelete(p._id, p.name)} className="p-2 text-white/40 hover:text-red-400 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}


// ─── Player Form ───
function PlayerForm({ player, onClose, onSave }) {
  const [fd, setFd] = useState({ name: player?.name || '', number: player?.number || '', position: player?.position || 'Stürmer', nationality: player?.nationality || 'Deutschland', height: player?.height || '', weight: player?.weight || '', shoots: player?.shoots || 'Links', bio: player?.bio || '' });
  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState(player?.photoUrl ? getImageUrl(player.photoUrl) : '');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const formData = new FormData();
      Object.entries(fd).forEach(([k, v]) => formData.append(k, v));
      if (photo) formData.append('photo', photo);
      if (player?._id) { await adminApi.updatePlayer(player._id, formData); toast.success('Spieler aktualisiert'); }
      else { await adminApi.createPlayer(formData); toast.success('Spieler erstellt'); }
      onSave();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehler'); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-[hsl(0,0%,8%)] border border-white/10 rounded-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>{player ? 'Spieler bearbeiten' : 'Neuer Spieler'}</h3>
          <button onClick={onClose} className="p-1 text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex items-start gap-4">
            <div className="w-24 h-32 rounded-lg overflow-hidden bg-[hsl(0,0%,12%)] border border-white/10 flex-shrink-0">
              {preview ? <img src={preview} alt="Preview" className="w-full h-full object-cover" /> : <Upload className="w-6 h-6 text-white/20 m-auto mt-12" />}
            </div>
            <label className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-lg cursor-pointer hover:bg-white/10">
              <Upload className="w-4 h-4 text-white/60" /><span className="text-sm text-white/60">Foto</span>
              <input type="file" accept="image/*" onChange={e => { const f = e.target.files?.[0]; if (f) { setPhoto(f); setPreview(URL.createObjectURL(f)); }}} className="hidden" data-testid="admin-player-photo-input" />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm text-white/60 mb-1">Name *</label><input type="text" value={fd.name} onChange={e => setFd({...fd, name: e.target.value})} className="w-full px-3 py-2 bg-[hsl(0,0%,12%)] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#46C7FF]" required data-testid="admin-player-name-input" /></div>
            <div><label className="block text-sm text-white/60 mb-1">Nummer *</label><input type="number" value={fd.number} onChange={e => setFd({...fd, number: e.target.value})} className="w-full px-3 py-2 bg-[hsl(0,0%,12%)] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#46C7FF]" required data-testid="admin-player-number-input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm text-white/60 mb-1">Position *</label><select value={fd.position} onChange={e => setFd({...fd, position: e.target.value})} className="w-full px-3 py-2 bg-[hsl(0,0%,12%)] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#46C7FF]" data-testid="admin-player-position-select"><option value="Stürmer">Stürmer</option><option value="Verteidiger">Verteidiger</option><option value="Torwart">Torwart</option><option value="Trainer">Trainer</option><option value="Betreuer">Betreuer</option><option value="Staff">Staff</option></select></div>
            <div><label className="block text-sm text-white/60 mb-1">Schusshand</label><select value={fd.shoots} onChange={e => setFd({...fd, shoots: e.target.value})} className="w-full px-3 py-2 bg-[hsl(0,0%,12%)] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#46C7FF]"><option value="Links">Links</option><option value="Rechts">Rechts</option></select></div>
          </div>
          <div>
            <label className="block text-sm text-white/60 mb-1">Beschreibung</label>
            <textarea value={fd.bio} onChange={e => setFd({...fd, bio: e.target.value})} rows={3}
              className="w-full px-3 py-2 bg-[hsl(0,0%,12%)] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#46C7FF] resize-none"
              placeholder="Kurze Spieler-Biografie..." data-testid="admin-player-bio-input" />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 bg-white/5 text-white border border-white/10 rounded-lg text-sm">Abbrechen</button>
            <button type="submit" disabled={saving} className="flex-1 py-2.5 bg-[#CC0000] text-white rounded-lg text-sm font-semibold hover:bg-[#B30000] disabled:opacity-50" data-testid="admin-players-save-button">{saving ? 'Speichern...' : (player ? 'Aktualisieren' : 'Erstellen')}</button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}


// ─── Teams Tab (NEW) ───
function TeamsTab() {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);

  const loadTeams = useCallback(() => {
    publicApi.getTeams().then(res => { setTeams(res.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);
  useEffect(() => { loadTeams(); }, [loadTeams]);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Team "${name}" wirklich löschen?`)) return;
    try { await adminApi.deleteTeam(id); toast.success(`${name} gelöscht`); loadTeams(); } catch { toast.error('Fehler'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>Gegnerische Mannschaften</h2>
          <p className="text-xs text-white/40 mt-1">Teams hier anlegen, um sie bei Spielen direkt auszuwählen</p>
        </div>
        <button onClick={() => { setEditingTeam(null); setShowForm(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-[#CC0000] text-white text-sm font-semibold rounded-lg hover:bg-[#B30000]"
          data-testid="admin-teams-create-button">
          <Plus className="w-4 h-4" />Team hinzufügen
        </button>
      </div>
      {showForm && <TeamForm team={editingTeam} onClose={() => { setShowForm(false); setEditingTeam(null); }} onSave={() => { setShowForm(false); setEditingTeam(null); loadTeams(); }} />}
      {loading ? <div className="bg-[hsl(0,0%,8%)] border border-white/10 rounded-xl p-8 animate-pulse"><div className="h-6 bg-white/5 rounded w-1/3" /></div>
       : teams.length === 0 ? <div className="bg-[hsl(0,0%,8%)] border border-white/10 rounded-xl p-16 text-center"><ShieldIcon className="w-12 h-12 text-white/15 mx-auto mb-4" /><p className="text-white/50 text-sm">Noch keine Teams angelegt</p></div>
       : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {teams.map(team => (
            <div key={team._id} className="bg-[hsl(0,0%,8%)] border border-white/10 rounded-xl p-4 flex items-center gap-4 hover:border-white/20 transition-colors duration-200">
              <div className="w-14 h-14 rounded-full overflow-hidden bg-[hsl(0,0%,12%)] flex-shrink-0 border border-white/10">
                {team.logoUrl ? <img src={getImageUrl(team.logoUrl)} alt={team.name} className="w-full h-full object-contain p-1" style={{ background: '#141414' }} /> : <ShieldIcon className="w-6 h-6 text-white/20 m-4" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-white truncate" style={{ fontFamily: 'Space Grotesk' }}>{team.name}</div>
                <div className="text-xs text-white/40">{team.shortName} {team.city ? `• ${team.city}` : ''}</div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => { setEditingTeam(team); setShowForm(true); }} className="p-2 text-white/40 hover:text-[#46C7FF] rounded-lg"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => handleDelete(team._id, team.name)} className="p-2 text-white/40 hover:text-red-400 rounded-lg"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ─── Team Form ───
function TeamForm({ team, onClose, onSave }) {
  const [fd, setFd] = useState({ name: team?.name || '', shortName: team?.shortName || '', city: team?.city || '' });
  const [logo, setLogo] = useState(null);
  const [preview, setPreview] = useState(team?.logoUrl ? getImageUrl(team.logoUrl) : '');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const formData = new FormData();
      Object.entries(fd).forEach(([k, v]) => formData.append(k, v));
      if (logo) formData.append('logo', logo);
      if (team?._id) { await adminApi.updateTeam(team._id, formData); toast.success('Team aktualisiert'); }
      else { await adminApi.createTeam(formData); toast.success('Team erstellt'); }
      onSave();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehler'); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-[hsl(0,0%,8%)] border border-white/10 rounded-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>{team ? 'Team bearbeiten' : 'Neues Team'}</h3>
          <button onClick={onClose} className="p-1 text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full overflow-hidden bg-[hsl(0,0%,12%)] border border-white/10 flex-shrink-0">
              {preview ? <img src={preview} alt="Logo" className="w-full h-full object-contain p-1" style={{ background: '#141414' }} /> : <ShieldIcon className="w-6 h-6 text-white/20 m-5" />}
            </div>
            <label className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-lg cursor-pointer hover:bg-white/10">
              <Upload className="w-4 h-4 text-white/60" /><span className="text-sm text-white/60">Logo</span>
              <input type="file" accept="image/*" onChange={e => { const f = e.target.files?.[0]; if (f) { setLogo(f); setPreview(URL.createObjectURL(f)); }}} className="hidden" />
            </label>
          </div>
          <div><label className="block text-sm text-white/60 mb-1">Teamname *</label><input type="text" value={fd.name} onChange={e => setFd({...fd, name: e.target.value})} className="w-full px-3 py-2 bg-[hsl(0,0%,12%)] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#46C7FF]" placeholder="z.B. Lausitzer Füchse" required /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm text-white/60 mb-1">Kürzel</label><input type="text" value={fd.shortName} onChange={e => setFd({...fd, shortName: e.target.value})} className="w-full px-3 py-2 bg-[hsl(0,0%,12%)] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#46C7FF]" placeholder="z.B. LFX" maxLength={5} /></div>
            <div><label className="block text-sm text-white/60 mb-1">Stadt</label><input type="text" value={fd.city} onChange={e => setFd({...fd, city: e.target.value})} className="w-full px-3 py-2 bg-[hsl(0,0%,12%)] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#46C7FF]" placeholder="z.B. Weißwasser" /></div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 bg-white/5 text-white border border-white/10 rounded-lg text-sm">Abbrechen</button>
            <button type="submit" disabled={saving} className="flex-1 py-2.5 bg-[#CC0000] text-white rounded-lg text-sm font-semibold hover:bg-[#B30000] disabled:opacity-50">{saving ? 'Speichern...' : (team ? 'Aktualisieren' : 'Erstellen')}</button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}


// ─── Games Tab ───
function GamesTab() {
  const [games, setGames] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingGame, setEditingGame] = useState(null);

  const loadData = useCallback(() => {
    Promise.all([publicApi.getGames(), publicApi.getTeams()])
      .then(([gRes, tRes]) => { setGames(gRes.data); setTeams(tRes.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);
  useEffect(() => { loadData(); }, [loadData]);

  const handleDelete = async (id, opponent) => {
    if (!window.confirm(`Spiel gegen "${opponent}" wirklich löschen?`)) return;
    try { await adminApi.deleteGame(id); toast.success('Spiel gelöscht'); loadData(); } catch { toast.error('Fehler'); }
  };

  const getTeamLogo = (game) => {
    if (game.opponentTeamId) {
      const team = teams.find(t => t._id === game.opponentTeamId);
      if (team?.logoUrl) return getImageUrl(team.logoUrl);
    }
    return null;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>Spiele verwalten</h2>
        <button onClick={() => { setEditingGame(null); setShowForm(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-[#CC0000] text-white text-sm font-semibold rounded-lg hover:bg-[#B30000]"
          data-testid="admin-games-create-button">
          <Plus className="w-4 h-4" />Spiel hinzufügen
        </button>
      </div>
      {showForm && <GameForm game={editingGame} teams={teams} onClose={() => { setShowForm(false); setEditingGame(null); }} onSave={() => { setShowForm(false); setEditingGame(null); loadData(); }} />}
      {loading ? <div className="bg-[hsl(0,0%,8%)] border border-white/10 rounded-xl p-8 animate-pulse"><div className="h-6 bg-white/5 rounded w-1/3" /></div>
       : games.length === 0 ? <div className="bg-[hsl(0,0%,8%)] border border-white/10 rounded-xl p-16 text-center"><Trophy className="w-12 h-12 text-white/15 mx-auto mb-4" /><p className="text-white/50 text-sm">Noch keine Spiele</p></div>
       : (
        <div className="bg-[hsl(0,0%,8%)] border border-white/10 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr className="border-b border-white/10">
                <th className="text-left text-xs text-white/40 font-medium px-4 py-3">Datum</th>
                <th className="text-left text-xs text-white/40 font-medium px-4 py-3">Gegner</th>
                <th className="text-left text-xs text-white/40 font-medium px-4 py-3">Saison</th>
                <th className="text-left text-xs text-white/40 font-medium px-4 py-3">Ort</th>
                <th className="text-left text-xs text-white/40 font-medium px-4 py-3">Status</th>
                <th className="text-left text-xs text-white/40 font-medium px-4 py-3">Ergebnis</th>
                <th className="text-right text-xs text-white/40 font-medium px-4 py-3">Aktionen</th>
              </tr></thead>
              <tbody>
                {games.map(game => (
                  <tr key={game._id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="px-4 py-3 text-sm text-white">{game.date ? new Date(game.date).toLocaleDateString('de-DE') : '-'}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {getTeamLogo(game) && <img src={getTeamLogo(game)} alt="" className="w-6 h-6 rounded-full object-contain" style={{ background: '#141414' }} />}
                        <span className="text-sm font-medium text-white">{game.opponent}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-white/40 font-mono-score">{game.season || '-'}</td>
                    <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full ${game.location === 'Heim' ? 'bg-green-400/10 text-green-400' : 'bg-yellow-400/10 text-yellow-400'}`}>{game.location}</span></td>
                    <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full ${game.status === 'upcoming' ? 'bg-[#46C7FF]/10 text-[#46C7FF]' : 'bg-white/10 text-white/60'}`}>{game.status === 'upcoming' ? 'Kommend' : 'Gespielt'}</span></td>
                    <td className="px-4 py-3">{game.status === 'played' && game.scoreFor != null ? <span className="text-sm font-bold text-white font-mono-score">{game.scoreFor}:{game.scoreAgainst}</span> : <span className="text-sm text-white/30">-</span>}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => { setEditingGame(game); setShowForm(true); }} className="p-2 text-white/40 hover:text-[#46C7FF] rounded-lg"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => handleDelete(game._id, game.opponent)} className="p-2 text-white/40 hover:text-red-400 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}


// ─── Game Form (with team selection + season) ───
function GameForm({ game, teams, onClose, onSave }) {
  const [fd, setFd] = useState({
    opponent: game?.opponent || '',
    opponentTeamId: game?.opponentTeamId || '',
    date: game?.date || '',
    time: game?.time || '19:30',
    location: game?.location || 'Heim',
    status: game?.status || 'upcoming',
    season: game?.season || '2025/2026',
    scoreFor: game?.scoreFor ?? '',
    scoreAgainst: game?.scoreAgainst ?? '',
  });
  const [saving, setSaving] = useState(false);

  const handleTeamSelect = (teamId) => {
    const team = teams.find(t => t._id === teamId);
    if (team) {
      setFd({ ...fd, opponentTeamId: teamId, opponent: team.name });
    } else {
      setFd({ ...fd, opponentTeamId: '', opponent: '' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const data = { ...fd, scoreFor: fd.status === 'played' && fd.scoreFor !== '' ? parseInt(fd.scoreFor) : null, scoreAgainst: fd.status === 'played' && fd.scoreAgainst !== '' ? parseInt(fd.scoreAgainst) : null };
      if (game?._id) { await adminApi.updateGame(game._id, data); toast.success('Spiel aktualisiert'); }
      else { await adminApi.createGame(data); toast.success('Spiel erstellt'); }
      onSave();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehler'); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-[hsl(0,0%,8%)] border border-white/10 rounded-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>{game ? 'Spiel bearbeiten' : 'Neues Spiel'}</h3>
          <button onClick={onClose} className="p-1 text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Team selection */}
          {teams.length > 0 && (
            <div>
              <label className="block text-sm text-white/60 mb-1">Team auswählen</label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-2 max-h-40 overflow-y-auto">
                {teams.map(team => (
                  <button key={team._id} type="button" onClick={() => handleTeamSelect(team._id)}
                    className={`flex items-center gap-2 p-2 rounded-lg border text-left text-xs transition-colors duration-200 ${fd.opponentTeamId === team._id ? 'border-[#CC0000] bg-[#CC0000]/10' : 'border-white/10 hover:border-white/20 bg-white/[0.02]'}`}>
                    <div className="w-8 h-8 rounded-full overflow-hidden bg-[hsl(0,0%,12%)] flex-shrink-0">
                      {team.logoUrl ? <img src={getImageUrl(team.logoUrl)} alt="" className="w-full h-full object-contain p-0.5" style={{ background: '#141414' }} /> : <ShieldIcon className="w-3 h-3 text-white/20 m-2.5" />}
                    </div>
                    <span className="text-white/80 truncate">{team.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          <div><label className="block text-sm text-white/60 mb-1">Gegner {teams.length > 0 ? '(oder manuell)' : '*'}</label><input type="text" value={fd.opponent} onChange={e => setFd({...fd, opponent: e.target.value})} className="w-full px-3 py-2 bg-[hsl(0,0%,12%)] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#46C7FF]" placeholder="z.B. Lausitzer Füchse" required data-testid="admin-game-opponent-input" /></div>
          <div className="grid grid-cols-3 gap-4">
            <div><label className="block text-sm text-white/60 mb-1">Datum *</label><input type="date" value={fd.date} onChange={e => setFd({...fd, date: e.target.value})} className="w-full px-3 py-2 bg-[hsl(0,0%,12%)] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#46C7FF]" required data-testid="admin-game-date-input" /></div>
            <div><label className="block text-sm text-white/60 mb-1">Uhrzeit</label><input type="time" value={fd.time} onChange={e => setFd({...fd, time: e.target.value})} className="w-full px-3 py-2 bg-[hsl(0,0%,12%)] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#46C7FF]" /></div>
            <div><label className="block text-sm text-white/60 mb-1">Saison</label><input type="text" value={fd.season} onChange={e => setFd({...fd, season: e.target.value})} className="w-full px-3 py-2 bg-[hsl(0,0%,12%)] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#46C7FF]" placeholder="2025/2026" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="block text-sm text-white/60 mb-1">Ort</label><select value={fd.location} onChange={e => setFd({...fd, location: e.target.value})} className="w-full px-3 py-2 bg-[hsl(0,0%,12%)] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#46C7FF]" data-testid="admin-game-location-select"><option value="Heim">Heim</option><option value="Auswärts">Auswärts</option></select></div>
            <div><label className="block text-sm text-white/60 mb-1">Status</label><select value={fd.status} onChange={e => setFd({...fd, status: e.target.value})} className="w-full px-3 py-2 bg-[hsl(0,0%,12%)] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#46C7FF]" data-testid="admin-game-status-select"><option value="upcoming">Kommend</option><option value="played">Gespielt</option></select></div>
          </div>
          {fd.status === 'played' && (
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-sm text-white/60 mb-1">Tore Devils</label><input type="number" min="0" value={fd.scoreFor} onChange={e => setFd({...fd, scoreFor: e.target.value})} className="w-full px-3 py-2 bg-[hsl(0,0%,12%)] border border-white/10 rounded-lg text-white text-sm font-mono-score focus:outline-none focus:ring-2 focus:ring-[#46C7FF]" data-testid="admin-game-score-for-input" /></div>
              <div><label className="block text-sm text-white/60 mb-1">Tore Gegner</label><input type="number" min="0" value={fd.scoreAgainst} onChange={e => setFd({...fd, scoreAgainst: e.target.value})} className="w-full px-3 py-2 bg-[hsl(0,0%,12%)] border border-white/10 rounded-lg text-white text-sm font-mono-score focus:outline-none focus:ring-2 focus:ring-[#46C7FF]" data-testid="admin-game-score-against-input" /></div>
            </div>
          )}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 bg-white/5 text-white border border-white/10 rounded-lg text-sm">Abbrechen</button>
            <button type="submit" disabled={saving} className="flex-1 py-2.5 bg-[#CC0000] text-white rounded-lg text-sm font-semibold hover:bg-[#B30000] disabled:opacity-50" data-testid="admin-games-save-button">{saving ? 'Speichern...' : (game ? 'Aktualisieren' : 'Erstellen')}</button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}


// ─── Stats Tab (NEW) ───
function StatsTab() {
  const [opponentStats, setOpponentStats] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState('');
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(() => {
    setLoading(true);
    Promise.all([
      adminApi.getOpponentStats(selectedSeason || undefined),
      adminApi.getSeasons(),
    ]).then(([statsRes, seasonsRes]) => {
      setOpponentStats(statsRes.data);
      setSeasons(seasonsRes.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [selectedSeason]);

  useEffect(() => { loadData(); }, [loadData]);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>Spielstatistik pro Gegner</h2>
          <p className="text-xs text-white/40 mt-1">Spielhistorie gegen jeden Gegner, filterbar nach Saison</p>
        </div>
        {seasons.length > 0 && (
          <select value={selectedSeason} onChange={e => setSelectedSeason(e.target.value)}
            className="px-3 py-2 bg-[hsl(0,0%,12%)] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#46C7FF]">
            <option value="">Alle Saisons</option>
            {seasons.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        )}
      </div>

      {loading ? <div className="bg-[hsl(0,0%,8%)] border border-white/10 rounded-xl p-8 animate-pulse"><div className="h-6 bg-white/5 rounded w-1/3" /></div>
       : opponentStats.length === 0 ? <div className="bg-[hsl(0,0%,8%)] border border-white/10 rounded-xl p-16 text-center"><BarChart3 className="w-12 h-12 text-white/15 mx-auto mb-4" /><p className="text-white/50 text-sm">Noch keine Spielstatistiken</p></div>
       : (
        <div className="space-y-4">
          {opponentStats.sort((a, b) => (b.wins + b.losses + b.draws) - (a.wins + a.losses + a.draws)).map((opp, i) => {
            const total = opp.wins + opp.losses + opp.draws;
            const winPct = total > 0 ? Math.round((opp.wins / total) * 100) : 0;
            return (
              <motion.div key={opp.opponent} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                className="bg-[hsl(0,0%,8%)] border border-white/10 rounded-xl overflow-hidden">
                <div className="p-4 sm:p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="text-base font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>{opp.opponent}</div>
                    </div>
                    <div className="flex items-center gap-4 text-xs">
                      <span className="text-green-400 font-bold">{opp.wins}S</span>
                      <span className="text-red-400 font-bold">{opp.losses}N</span>
                      <span className="text-yellow-400 font-bold">{opp.draws}U</span>
                      <span className="text-white/30 font-mono-score">{opp.goalsFor}:{opp.goalsAgainst}</span>
                    </div>
                  </div>
                  {/* Win percentage bar */}
                  <div className="h-1.5 bg-white/5 rounded-full overflow-hidden mb-3">
                    <div className="h-full rounded-full" style={{ width: `${winPct}%`, background: winPct > 50 ? '#4ade80' : winPct > 30 ? '#fbbf24' : '#f87171' }} />
                  </div>
                  {/* Game history */}
                  <div className="flex flex-wrap gap-1.5">
                    {opp.games.sort((a, b) => b.date?.localeCompare(a.date)).map((g, gi) => {
                      const w = g.scoreFor > g.scoreAgainst;
                      const l = g.scoreFor < g.scoreAgainst;
                      return (
                        <div key={gi} className={`text-[9px] px-2 py-0.5 rounded font-mono-score ${w ? 'bg-green-500/10 text-green-400' : l ? 'bg-red-500/10 text-red-400' : 'bg-yellow-500/10 text-yellow-400'}`}>
                          {g.scoreFor}:{g.scoreAgainst} {g.location === 'Heim' ? 'H' : 'A'} {g.date ? new Date(g.date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }) : ''}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}



// ─── Club Logo Upload ───
function ClubLogoUpload({ logoUrl }) {
  const [uploading, setUploading] = useState(false);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('logo', file);
      await adminApi.uploadClubLogo(formData);
      toast.success('Vereinslogo aktualisiert – Seite wird neu geladen...');
      setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      toast.error('Fehler beim Logo-Upload');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="bg-[hsl(0,0%,8%)] border border-white/10 rounded-xl p-4 mb-6 flex items-center justify-between">
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-full overflow-hidden bg-[hsl(0,0%,12%)] border border-white/10 flex-shrink-0">
          {logoUrl ? (
            <img src={logoUrl} alt="Vereinslogo" className="w-full h-full object-contain rounded-full" style={{ background: '#141414' }} />
          ) : (
            <ShieldIcon className="w-5 h-5 text-white/20 m-3.5" />
          )}
        </div>
        <div>
          <div className="text-sm font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }}>Vereinslogo</div>
          <div className="text-xs text-white/40">Wird überall auf der Website angezeigt</div>
        </div>
      </div>
      <label className={`flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer transition-colors duration-200 text-sm font-medium ${uploading ? 'bg-white/5 text-white/30' : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white/80 border border-white/10'}`}>
        <Upload className="w-4 h-4" />
        {uploading ? 'Wird hochgeladen...' : 'Logo ändern'}
        <input type="file" accept="image/jpeg,image/png,image/webp,image/svg+xml" onChange={handleLogoUpload} className="hidden" disabled={uploading} data-testid="admin-club-logo-upload" />
      </label>
    </div>
  );
}


// ─── Hero Background Upload ───
function HeroBgUpload() {
  const [uploading, setUploading] = useState(false);
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      await adminApi.uploadHeroBg(formData);
      toast.success('Hintergrundbild aktualisiert');
    } catch { toast.error('Fehler'); } finally { setUploading(false); }
  };
  return (
    <div className="bg-[hsl(0,0%,8%)] border border-white/10 rounded-xl p-4 mb-6 flex items-center justify-between">
      <div>
        <div className="text-sm font-semibold text-white" style={{ fontFamily: 'Space Grotesk' }}>Hintergrundbild (Hero)</div>
        <div className="text-xs text-white/40">Mannschaftsfoto auf der Startseite</div>
      </div>
      <label className={`flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer text-sm font-medium ${uploading ? 'bg-white/5 text-white/30' : 'bg-white/5 text-white/60 hover:bg-white/10 border border-white/10'}`}>
        <Upload className="w-4 h-4" />{uploading ? 'Hochladen...' : 'Bild ändern'}
        <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleUpload} className="hidden" disabled={uploading} />
      </label>
    </div>
  );
}


// ─── Sponsors Tab ───
function SponsorsTab() {
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const loadSponsors = useCallback(() => {
    publicApi.getSponsors().then(res => { setSponsors(res.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);
  useEffect(() => { loadSponsors(); }, [loadSponsors]);

  const [name, setName] = useState('');
  const [link, setLink] = useState('');
  const [logo, setLogo] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!name) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('name', name); fd.append('link', link); fd.append('order', sponsors.length);
      if (logo) fd.append('logo', logo);
      await adminApi.createSponsor(fd);
      toast.success('Sponsor hinzugefügt');
      setName(''); setLink(''); setLogo(null);
      loadSponsors();
    } catch { toast.error('Fehler'); } finally { setSaving(false); }
  };

  const handleDelete = async (id, n) => {
    if (!window.confirm(`Sponsor "${n}" löschen?`)) return;
    await adminApi.deleteSponsor(id); toast.success('Gelöscht'); loadSponsors();
  };

  return (
    <div>
      <h2 className="text-lg font-bold text-white mb-6" style={{ fontFamily: 'Space Grotesk' }}>Sponsoren verwalten</h2>
      <form onSubmit={handleAdd} className="bg-[hsl(0,0%,8%)] border border-white/10 rounded-xl p-4 mb-6 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[150px]"><label className="block text-xs text-white/40 mb-1">Name *</label><input type="text" value={name} onChange={e => setName(e.target.value)} className="w-full px-3 py-2 bg-[hsl(0,0%,12%)] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#46C7FF]" required /></div>
        <div className="flex-1 min-w-[150px]"><label className="block text-xs text-white/40 mb-1">Link</label><input type="text" value={link} onChange={e => setLink(e.target.value)} className="w-full px-3 py-2 bg-[hsl(0,0%,12%)] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#46C7FF]" placeholder="https://..." /></div>
        <label className="flex items-center gap-2 px-3 py-2 bg-white/5 border border-white/10 rounded-lg cursor-pointer text-sm text-white/60 hover:bg-white/10">
          <Upload className="w-4 h-4" />{logo ? logo.name.substring(0, 15) : 'Logo'}
          <input type="file" accept="image/*" onChange={e => setLogo(e.target.files?.[0])} className="hidden" />
        </label>
        <button type="submit" disabled={saving} className="px-4 py-2 bg-[#CC0000] text-white text-sm font-semibold rounded-lg hover:bg-[#B30000] disabled:opacity-50"><Plus className="w-4 h-4 inline mr-1" />Hinzufügen</button>
      </form>
      {loading ? <div className="animate-pulse h-20 bg-white/5 rounded-xl" /> : sponsors.length === 0 ? <p className="text-white/30 text-sm text-center py-8">Noch keine Sponsoren</p> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {sponsors.map(s => (
            <div key={s._id} className="bg-[hsl(0,0%,8%)] border border-white/10 rounded-xl p-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded bg-[hsl(0,0%,12%)] overflow-hidden flex items-center justify-center">
                  {s.logoMime ? <img src={getImageUrl(`/api/public/sponsors/${s._id}/logo`)} alt="" className="w-full h-full object-contain p-1" /> : <ShieldIcon className="w-4 h-4 text-white/20" />}
                </div>
                <div><div className="text-sm text-white font-medium">{s.name}</div>{s.link && <div className="text-[10px] text-white/30 truncate max-w-[120px]">{s.link}</div>}</div>
              </div>
              <button onClick={() => handleDelete(s._id, s.name)} className="p-2 text-white/40 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ─── Gallery Tab ───
function GalleryTab() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const loadGallery = useCallback(() => {
    publicApi.getGallery().then(res => { setImages(res.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);
  useEffect(() => { loadGallery(); }, [loadGallery]);

  const [title, setTitle] = useState('');
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!file) { toast.error('Bitte ein Bild auswählen'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('title', title); fd.append('order', images.length); fd.append('image', file);
      await adminApi.addGalleryImage(fd);
      toast.success('Bild hinzugefügt');
      setTitle(''); setFile(null); loadGallery();
    } catch { toast.error('Fehler'); } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bild wirklich löschen?')) return;
    await adminApi.deleteGalleryImage(id); toast.success('Gelöscht'); loadGallery();
  };

  return (
    <div>
      <h2 className="text-lg font-bold text-white mb-6" style={{ fontFamily: 'Space Grotesk' }}>Bildergalerie verwalten</h2>
      <form onSubmit={handleAdd} className="bg-[hsl(0,0%,8%)] border border-white/10 rounded-xl p-4 mb-6 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[150px]"><label className="block text-xs text-white/40 mb-1">Titel (optional)</label><input type="text" value={title} onChange={e => setTitle(e.target.value)} className="w-full px-3 py-2 bg-[hsl(0,0%,12%)] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#46C7FF]" /></div>
        <label className="flex items-center gap-2 px-3 py-2 bg-white/5 border border-white/10 rounded-lg cursor-pointer text-sm text-white/60 hover:bg-white/10">
          <Upload className="w-4 h-4" />{file ? file.name.substring(0, 20) : 'Bild auswählen'}
          <input type="file" accept="image/*" onChange={e => setFile(e.target.files?.[0])} className="hidden" />
        </label>
        <button type="submit" disabled={saving || !file} className="px-4 py-2 bg-[#CC0000] text-white text-sm font-semibold rounded-lg hover:bg-[#B30000] disabled:opacity-50"><Plus className="w-4 h-4 inline mr-1" />Hochladen</button>
      </form>
      {loading ? <div className="animate-pulse h-20 bg-white/5 rounded-xl" /> : images.length === 0 ? <p className="text-white/30 text-sm text-center py-8">Noch keine Galerie-Bilder</p> : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {images.map(img => (
            <div key={img._id} className="relative group rounded-xl overflow-hidden bg-[hsl(0,0%,8%)] border border-white/10">
              <img src={getImageUrl(`/api/public/gallery/${img._id}/image`)} alt={img.title || ''} className="w-full aspect-video object-cover" />
              {img.title && <div className="px-2 py-1 text-xs text-white/60 truncate">{img.title}</div>}
              <button onClick={() => handleDelete(img._id)} className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 flex items-center justify-center text-white/60 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
